import { useNavigation } from '@react-navigation/native'
import * as Clipboard from 'expo-clipboard'
import moment from 'moment'
import { Platform, View } from 'react-native'
import { Card, Icon, Text } from 'react-native-paper'
import Toast from 'react-native-root-toast'
import { useTranslation } from 'react-i18next'
import Row from '../../../../@ui/containers/grid/row'
import { createStyle } from '../../../../contexts/global-styles.provider'
import colors from '../../../../constants/colors'
import { TouchableIconWithToolTip } from '../../../../utils/meldd-tooltip'
import { AppNavigation } from '../../../../navigation/types'
import { Node } from '../../../../generated/graphql'
import { NodeTierStatusBadge } from '../../payment/components/node-tier-status.badge'

interface NodeCardProps {
  node: Node & { memberCount: number }
  onEdit?: () => void
  onDelete?: () => void
}

export const NodeCard = ({ node }: NodeCardProps) => {
  const styles = useStyle()
  const { t } = useTranslation('nodes')
  const navigation = useNavigation<AppNavigation>()

  const copy = async () => {
    await Clipboard.setStringAsync(
      Platform.OS === 'web'
        ? `${location.href.split('#')[0]}#/node/${node.id}`
        : `meldd://node/${node.id}`
    )
    Toast.show(t('copied'))
  }

  const onEdit = () => {
    navigation.navigate('NodeSettings', { nodeId: node.id })
  }

  const parsedDate = moment(node.createdAt).format('YYYY-MM-DD')

  return (
    <Card
      style={{ paddingVertical: 10, paddingHorizontal: 20 }}
      onPress={onEdit}
    >
      {/* HEADER */}
      <Row justifyContent={'space-between'} wrap>
        <Text variant={'titleLarge'}>{node.name}</Text>

        <Row alignItems={'center'} columnGap={2}>
          <Icon source={node.isPrivate ? 'lock' : 'earth'} size={18} />
          <Text style={[styles.statusText]}>
            {node.isPrivate ? t('private') : t('public')}
          </Text>
          {node.isPrivate && (
            <>
              <Icon source="account-group" size={18} color={colors.grey600} />{' '}
              <Text>{node.memberCount}</Text>
            </>
          )}
          <NodeTierStatusBadge activeTier={node.activeTier} />
        </Row>
      </Row>

      {/* DESCRIPTION */}
      <Row justifyContent={'space-between'} wrap>
        <View>
          {node.description && (
            <Text variant={'bodyMedium'} style={styles.description}>
              {node.description}
            </Text>
          )}
          <View>
            <Text variant={'bodyMedium'} style={styles.sectionLabel}>
              {t('created')}
              {': '}
              <Text style={styles.dateText}>{parsedDate}</Text>
            </Text>
          </View>
        </View>
        {/* ACTIONS */}
        <Row columnGap={4}>
          <TouchableIconWithToolTip
            icon={<Icon source="content-copy" size={18} />}
            tooltip={t('copy')}
            mode="text"
            onPress={copy}
          />

          <TouchableIconWithToolTip
            icon={<Icon source="eye" size={18} />}
            tooltip={t('view')}
            mode="text"
            onPress={() => {
              navigation.navigate('ClusterList', { nodeId: node.id })
            }}
          />

          {/* Manage Members icon, only for private nodes */}
          {node.isPrivate && (
            <TouchableIconWithToolTip
              icon={<Icon source="account-cog" size={18} />}
              tooltip={t('manage-members')}
              mode="text"
              onPress={() => {
                navigation.navigate('NodeMembers', { nodeId: node.id })
              }}
            />
          )}
        </Row>
      </Row>
    </Card>
  )
}

const useStyle = createStyle(({ theme }) => ({
  description: {
    marginTop: theme.spacing(2),
    color: colors.grey600,
  },
  sectionLabel: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(1),
    fontWeight: '500',
  },
  statusText: {
    fontWeight: '500',
  },
  memberCount: {
    color: colors.grey600,
  },
  memberChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.grey200,
    paddingHorizontal: theme.spacing(2),
    paddingVertical: theme.spacing(1),
    borderRadius: theme.spacing(4),
    columnGap: theme.spacing(1),
  },
  moreMembers: {
    color: colors.grey600,
    fontSize: 12,
    alignSelf: 'center',
  },
  dateText: {
    color: colors.grey600,
  },
}))

export default NodeCard
